import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { VhIconComponent } from '../icon/icon.component';

type PageItem = number | 'ellipsis';

/**
 * Numbered pagination. Desktop: windowed number list with ellipses.
 * Mobile: prev/next + "N of M". Parent owns `page` state.
 */
@Component({
  selector: 'vh-pagination',
  standalone: true,
  imports: [VhIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <nav class="vh-pagination" [attr.aria-label]="ariaLabel()">
      <button
        type="button"
        class="vh-pagination__nav"
        [disabled]="page() <= 1"
        (click)="go(page() - 1)"
        aria-label="Previous page"
      >
        <vh-icon name="chevron-down" size="sm" class="vh-pagination__prev-icon" />
        <span class="vh-pagination__nav-label">Previous</span>
      </button>

      <ol class="vh-pagination__pages">
        @for (item of pageItems(); track $index) {
          @if (item === 'ellipsis') {
            <li class="vh-pagination__ellipsis" aria-hidden="true">…</li>
          } @else {
            <li>
              <button
                type="button"
                class="vh-pagination__page"
                [class.vh-pagination__page--active]="item === page()"
                [attr.aria-current]="item === page() ? 'page' : null"
                [attr.aria-label]="'Page ' + item"
                (click)="go(item)"
              >
                {{ item }}
              </button>
            </li>
          }
        }
      </ol>

      <span class="vh-pagination__mobile">Page {{ page() }} of {{ totalPages() }}</span>

      <button
        type="button"
        class="vh-pagination__nav"
        [disabled]="page() >= totalPages()"
        (click)="go(page() + 1)"
        aria-label="Next page"
      >
        <span class="vh-pagination__nav-label">Next</span>
        <vh-icon name="chevron-down" size="sm" class="vh-pagination__next-icon" />
      </button>
    </nav>
  `,
  styleUrl: './pagination.component.scss',
})
export class VhPaginationComponent {
  readonly page = input<number>(1);
  readonly pageSize = input<number>(20);
  readonly total = input<number>(0);
  readonly ariaLabel = input<string>('Pagination');

  readonly pageChange = output<number>();

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.total() / this.pageSize())));

  readonly pageItems = computed<ReadonlyArray<PageItem>>(() => {
    const total = this.totalPages();
    const current = this.page();
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const items: PageItem[] = [1];
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    if (start > 2) items.push('ellipsis');
    for (let i = start; i <= end; i++) items.push(i);
    if (end < total - 1) items.push('ellipsis');
    items.push(total);
    return items;
  });

  go(page: number): void {
    const clamped = Math.min(Math.max(1, page), this.totalPages());
    if (clamped === this.page()) return;
    this.pageChange.emit(clamped);
  }
}
